<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref, watch } from 'vue'
import { useAnalytics } from '@/composables/analytics'
import {
    useEcommerce,
    useEcommercePrice,
    useLimitedTimeOffers,
} from '@/composables/game/ecommerce'
import { T } from '@tolgee/vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useConfetti } from '@/composables/game/confetti'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { syncPlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import ShopBadge from '@/components/ShopBadge.vue'
import afterStarterPackValueImage from '@/assets/images/shop-badge-500.png'
import turboStarterPackValueImage from '@/assets/images/shop-badge-400.png'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { formatDuration } from '@/helpers/date'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import PaymentButtons from '@/components/PaymentButtons.vue'

const props = defineProps<{
	afterStarterPack: EcommerceItem
	show: boolean
	coolDown: number
}>()

defineEmits<{
	(e: 'hide'): void
}>()

const { triggerEvent } = useAnalytics()
const { buildItemAnalyticsMetadata } = useEcommerce()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { afterStarterPackDuration } = useLimitedTimeOffers()
const { getStarPrice } = useEcommercePrice()

const starPriceItem = computed(() => getStarPrice(props.afterStarterPack))
const purchaseDone = ref(false)
const coinBenefit = computed(() => props.afterStarterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COIN))
const spinBenefit = computed(() => props.afterStarterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN))

const initialCooldown = ref(props.coolDown)

const formattedTime = computed(() => formatDuration(afterStarterPackDuration.value - props.coolDown))

async function handlePurchase(paid: boolean) {
  if (paid) {
    showConfetti()
    purchaseDone.value = true
    await syncPlayer()
    await updateFortuneWheelState()
}
}

watch(() => props.show, show => {
  if (!show) return
  const itemMetadata = buildItemAnalyticsMetadata(props.afterStarterPack)
  triggerEvent({
    event: 'view_item',
    metadata: {
      ecommerce: {
        currency: 'USD',
        value: itemMetadata.price,
        items: buildItemAnalyticsMetadata(props.afterStarterPack),
      },
    },
  })
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="max-[375px]:pb-6"
    @hide="$emit('hide')"
  >
    <div
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <div class="size-[114px] relative">
        <ShopBadge
          :value-image="initialCooldown ? afterStarterPackValueImage : turboStarterPackValueImage"
          outer-class="-left-1.5 -top-4 w-[70px] h-auto"
          valueable-image-class="top-3"
          value-image-class="top-[28px]"
          animate-value-image
        />
        <img
          class="w-full h-full object-cover"
          src="@/assets/images/shop-button.webp"
          alt="Shop button"
        >
      </div>
      <div class="mt-3 flex flex-col items-center">
        <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          <span v-if="purchaseDone">
            <T
              key-name="starter_pack.title_done"
              default-value="Purchase successful!"
            />
          </span>
          <template v-else>
            <T
              v-if="coolDown"
              key-name="after_starter_pack.title"
              default-value="Time-limited Pack"
            />
            <T
              v-else
              key-name="after_starter_pack.title2"
              default-value="Turbo Pack"
            />
            = ${{ starPriceItem?.usdAmount }}
          </template>
        </div>
        <div class="text-center text-white/60 text-base font-medium max-w-[328px]">
          <template v-if="purchaseDone">
            <T
              v-if="initialCooldown"
              key-name="after_starter_pack.title"
              default-value="Time-limited Pack"
            />
            <T
              v-else
              key-name="after_starter_pack.title2"
              default-value="Turbo Pack"
            />
          </template>
          <template v-else>
            <T
              v-if="coolDown"
              key-name="after_starter_pack.subtitle"
              default-value="Grab it before it's gone—supercharge your growth now!"
            />
            <T
              v-else
              key-name="after_starter_pack.subtitle2"
              default-value="Speed up your progress with the Turbo Pack!"
            />
          </template>
        </div>
      </div>
      <div class="max-[375px]:mt-4 mt-8 flex flex-col items-center gap-y-3 w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            :src="CoinIcon"
            class="w-5 h-auto min-w-[20px]"
            alt="Coin"
          >
          <NumberFormatTooltip
            :number="coinBenefit.amount"
            :formatting-type="'semi'"
          >
            <span
              class="benefit-box-value"
            >{{
              formatNumberSemiTruncate(coinBenefit.amount)
            }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && spinBenefit"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel.webp"
            class="size-8 min-w-[28px] object-cover"
            alt="Fortune wheel"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit.amount}"
            />
          </span>
        </div>
      </div>
      <div
        v-if="!purchaseDone && coolDown"
        class="mt-6 flex gap-y-1 flex-col items-center"
      >
        <div class="text-white/60 text-base font-medium">
          <T
            key-name="after_starter_pack.offer_ends_in"
            default-value="Offer ends in"
          />
        </div>
        <div
          class="rounded-full flex items-center justify-center px-8 py-[14px] bg-dark-most-bottom-layer text-white text-[24px] leading-5 font-medium -tracking-[0.48px]"
        >
          {{ formattedTime }}
        </div>
      </div>
      <BaseButton
        v-if="purchaseDone"
        class="w-full"
        :class="purchaseDone || (!purchaseDone && !coolDown) ? 'max-[375px]:mt-4 mt-8' : 'mt-6'"
        @click="$emit('hide')"
      >
        Close
      </BaseButton>
      <div
        v-else
        class="w-full"
        :class="purchaseDone || (!purchaseDone && !coolDown) ? 'max-[375px]:mt-4 mt-8' : 'mt-6'"
      >
        <PaymentButtons
          :item="afterStarterPack"
          @purchase="({ paid }) => handlePurchase(paid)"
        />
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";
</style>
